import type { Vec2 } from './math.ts';

export type Team = 'blue' | 'red';
export type HeroId = 'knight' | 'barbarian' | 'mage' | 'falco' | 'mario' | 'sheik';
export type AbilitySlot = 'q' | 'w' | 'e' | 'r';
export type ActionKind = 'idle' | 'attack' | 'cast' | 'hit' | 'dead';

export interface InputState {
  moveX: number;
  moveZ: number;
  /**
   * Destino del clic derecho: la simulación camina hacia él desde la posición
   * real del héroe y se para al llegar. Online, la dirección calculada en el
   * cliente sale de una posición con ~RTT de retraso y el héroe se pasaba del
   * destino; con el punto, el servidor frena justo.
   */
  moveTo?: { x: number; z: number };
  aimX: number;
  aimZ: number;
  attack: boolean;
  /** Enemigo fijado con clic izquierdo (0 = ninguno). */
  targetId: number;
  /** Flanco de la tecla B: empezar a volver a base. */
  recall: boolean;
  /** Subir un rango de habilidad (botón + / Shift+tecla); '' = nada. */
  levelUp: AbilitySlot | '';
  /** Comprar este objeto (id de ITEMS); '' = nada. */
  buy: string;
  /** Vender el objeto de esta casilla del inventario; -1 = nada. */
  sell: number;
  abilities: Record<AbilitySlot, boolean>;
}

export interface Cooldowns {
  q: number;
  w: number;
  e: number;
  r: number;
}

export interface ActionState {
  kind: ActionKind;
  slot?: AbilitySlot;
  ticks: number;
  duration: number;
  chain: number;
  hitIds: number[];
  /** dónde empezó el dash (hitbox `sweep`) */
  origin?: Vec2;
  /** agarre (R de Bowser): héroe agarrado y tick de la acción en que lo agarró */
  grabbedId?: number;
  /** objetivo de una ejecución (R de Link) */
  targetId?: number;
  grabTick?: number;
}

export interface HeroState {
  id: number;
  heroId: HeroId;
  team: Team;
  isPlayer: boolean;
  isBot: boolean;
  isDummy: boolean;
  name: string;
  pos: Vec2;
  prevPos: Vec2;
  vel: Vec2;
  spawnPos: Vec2;
  facing: number;
  aim: Vec2;
  hp: number;
  maxHp: number;
  radius: number;
  speed: number;
  alive: boolean;
  respawnAtTick: number;
  action: ActionState;
  actionSerial: number;
  cooldowns: Cooldowns;
  /** cargas disponibles por habilidad (sólo cuentan las que tienen `charges` > 1) */
  charges: Cooldowns;
  /** ticks hasta recuperar la siguiente carga (0 = no está recargando) */
  chargeTicks: Cooldowns;
  hitstun: number;
  invuln: number;
  chainWindow: number;
  /** ticks hasta poder lanzar el siguiente autoataque (1 / velocidad de ataque) */
  attackCooldown: number;
  /** golpe del combo del último autoataque (0..2) */
  comboChain: number;
  /** último héroe enemigo que lo dañó y cuándo (crédito de la kill, KILL_CREDIT_TICKS) */
  lastEnemyHeroId: number;
  lastEnemyHeroAt: number;
  /** héroes enemigos que lo dañaron recientemente (oro de asistencia, KILL_CREDIT_TICKS) */
  assistContributors: Array<{ heroId: number; at: number }>;
  flashTicks: number;
  deaths: number;
  kills: number;
  damageDealt: number;
  /** Tick en el que recibió daño por última vez (para la regeneración fuera de combate). */
  lastDamagedAt: number;
  /** Objetivo de autoataque actual (0 = ninguno). */
  targetId: number;
  /** Oro acumulado (minions rematados y héroes eliminados). */
  gold: number;
  /** >0 mientras canaliza el regreso a base (ticks transcurridos). */
  recallTicks: number;
  /** ticks restantes del escudo de espinas (devuelve parte del daño recibido) */
  thornsTicks: number;
  /** fracción del daño que devuelve el escudo de espinas activo */
  thornsReflect: number;
  /** escudo que absorbe daño (W de Bowser); se pierde al acabar `thornsTicks` */
  shieldHp: number;
  /** ticks transcurridos en el aire durante un agarre (0 = en el suelo) y duración total del vuelo */
  liftTicks: number;
  liftDuration: number;
  /** id del héroe que lo tiene agarrado (0 = libre) */
  grabbedBy: number;
  level: number;
  /** experiencia acumulada dentro del nivel actual */
  xp: number;
  skillPoints: number;
  /** rango de cada habilidad (0 = bloqueada) */
  ranks: Record<AbilitySlot, number>;
  /** inventario: ids de objetos (hasta 6) */
  items: string[];
  /** Línea asignada (bots). */
  lane?: number;
  /** Habilidad pulsada mientras otra acción la impedía; se lanza en cuanto se pueda. */
  bufferedSlot?: AbilitySlot;
  bufferedUntil?: number;
}

export interface MatchState {
  enabled: boolean;
  durationTicks: number;
  scoreToWin: number;
  elapsedTicks: number;
  score: Record<Team, number>;
  winner: Team | null;
  ended: boolean;
}

export type ProjectileKind = 'orb' | 'arrow' | 'missile' | 'needle' | 'fire' | 'bomb' | 'blackbomb' | 'blaster' | 'tower' | 'test';

export type StructureKind = 'tower' | 'nexus';

/** Torre o nexo. `tier` ordena la protección: 1 exterior, 2 interior, 3 nexo. */
export interface StructureState {
  id: number;
  kind: StructureKind;
  team: Team;
  tier: number;
  /** línea 0-2; -1 para el nexo */
  lane: number;
  /** Modelo de personaje usado como estatua plateada. */
  model: HeroId;
  pos: Vec2;
  radius: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  cooldown: number;
  targetId: number;
  facing: number;
}

export type MinionKind = 'koopa';

export interface MinionState {
  id: number;
  kind: MinionKind;
  team: Team;
  lane: number;
  /** índice del próximo punto del recorrido de su línea */
  waypoint: number;
  pos: Vec2;
  prevPos: Vec2;
  vel: Vec2;
  facing: number;
  radius: number;
  speed: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  cooldown: number;
  targetId: number;
  /** ticks desde el último golpe propio (para animar la embestida en el cliente) */
  attackTicks: number;
  /** perdió a un héroe que huyó de su área: va a la estructura enemiga más cercana de su línea */
  returning?: boolean;
}

export interface ProjectileState {
  id: number;
  ownerId: number;
  team: Team;
  kind: ProjectileKind;
  pos: Vec2;
  prevPos: Vec2;
  vel: Vec2;
  radius: number;
  damage: number;
  knockback: number;
  hitstun: number;
  ttl: number;
  alive: boolean;
  reflected: boolean;
  pierce: number;
  hitIds: number[];
  explodeRadius: number;
  explodeDamage: number;
  explodeKnockback: number;
  explodeHitstun: number;
  explodeOnExpire: boolean;
  explodeOnHit: boolean;
  /** Si > 0, el proyectil persigue a esta entidad (disparos de torre). */
  homingId: number;
  /** Habilidad cuya re-pulsación detona este proyectil (bomba de Link). */
  recastSlot?: AbilitySlot;
}

export type SimEvent =
  | {
      type: 'attack';
      sourceId: number;
      slot: 'attack' | AbilitySlot;
      x: number;
      z: number;
      facing: number;
      range: number;
      arc: number;
      heavy: boolean;
    }
  | {
      type: 'hit';
      sourceId: number;
      targetId: number;
      x: number;
      z: number;
      amount: number;
      heavy: boolean;
    }
  | { type: 'death'; heroId: number; killerId?: number; x: number; z: number }
  | { type: 'respawn'; heroId: number; x: number; z: number }
  | { type: 'matchEnd'; winner: Team; blue: number; red: number }
  | { type: 'projectileSpawn'; projectileId: number; x: number; z: number; kind: ProjectileKind }
  | { type: 'projectileHit'; projectileId: number; x: number; z: number; targetId?: number }
  | { type: 'projectileExplode'; projectileId: number; x: number; z: number; kind?: ProjectileKind; radius?: number }
  | { type: 'structureDestroyed'; structureId: number; team: Team; kind: StructureKind; x: number; z: number }
  | { type: 'minionDeath'; minionId: number; team: Team; x: number; z: number }
  | { type: 'gold'; heroId: number; amount: number; x: number; z: number }
  | { type: 'recall'; heroId: number; phase: 'start' | 'cancel' | 'done'; x: number; z: number }
  | { type: 'levelUp'; heroId: number; level: number; x: number; z: number }
  | { type: 'reflect'; heroId: number; x: number; z: number }
  /** R de Link: `mark` al lanzarla (cae en `delay` ticks), `strike` cuando la espada impacta */
  | { type: 'execute'; heroId: number; targetId: number; phase: 'mark' | 'strike'; x: number; z: number; delay: number; amount: number }
  | { type: 'shop'; heroId: number; action: 'buy' | 'sell'; itemId: string };

export function emptyInput(): InputState {
  return {
    moveX: 0,
    moveZ: 0,
    aimX: 0,
    aimZ: 1,
    attack: false,
    targetId: 0,
    recall: false,
    levelUp: '',
    buy: '',
    sell: -1,
    abilities: { q: false, w: false, e: false, r: false },
  };
}

export function emptyCooldowns(): Cooldowns {
  return { q: 0, w: 0, e: 0, r: 0 };
}

export function idleAction(): ActionState {
  return { kind: 'idle', ticks: 0, duration: 0, chain: 0, hitIds: [] };
}
